/**
 * CompanyPagesHub — "For Business" main landing
 * v4.0: Loan product circles (PrimeIcon + gradient), group-based feature cards,
 *       3-mode view (Discovery / HScroll / Grid), star ratings on cards.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Badge } from 'primereact/badge';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Avatar } from 'primereact/avatar';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import CreateCompanyPageDialog from '../../components/companyPages/CreateCompanyPageDialog';
import companyPagesBusinessService from '../../services/companyPagesBusinessService';
import NetworkSearch from '../../components/agentProfile/NetworkSearch';
import axiosInstance from '../../config/axios.config';
import ConnectionService from '../../services/agentProfile/ConnectionService';

/* ── Lookups ─────────────────────────────────────────────────────────── */
const ENTITY_LABEL = {
    bank:        'Bank',
    public_bank: 'Public Bank',
    nbfc:        'NBFC',
    fintech:     'Fintech',
    dsa:         'DSA',
    broker:      'Broker',
    legal:       'Legal',
    technical:   'Technical',
};

const ENTITY_SEVERITY = {
    bank:        'info',
    public_bank: 'info',
    nbfc:        'warning',
    fintech:     'help',
    dsa:         'success',
    broker:      'secondary',
    legal:       'success',
    technical:   'info',
};

// Solid counterpart to ENTITY_GRADIENT — used for the card's top rule, badge
// text and the banner wave, which all need one flat colour rather than a ramp.
const ENTITY_ACCENT = {
    bank:        '#2563eb',
    public_bank: '#3730a3',
    nbfc:        '#b45309',
    fintech:     '#6d28d9',
    dsa:         '#15803d',
    broker:      '#0d9488',
    legal:       '#9d174d',
    technical:   '#0369a1',
    default:     '#475569',
};

const ENTITY_GRADIENT = {
    bank:        'linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)',
    public_bank: 'linear-gradient(135deg, #1e3a8a 0%, #818cf8 100%)',
    nbfc:        'linear-gradient(135deg, #b45309 0%, #fbbf24 100%)',
    fintech:     'linear-gradient(135deg, #6d28d9 0%, #c084fc 100%)',
    dsa:         'linear-gradient(135deg, #15803d 0%, #4ade80 100%)',
    broker:      'linear-gradient(135deg, #0d9488 0%, #2dd4bf 100%)',
    legal:       'linear-gradient(135deg, #9d174d 0%, #f472b6 100%)',
    technical:   'linear-gradient(135deg, #0369a1 0%, #38bdf8 100%)',
    default:     'linear-gradient(135deg, #374151 0%, #9ca3af 100%)',
};

const ENTITY_ICON = {
    bank:        'pi pi-building',
    public_bank: 'pi pi-globe',
    nbfc:        'pi pi-chart-bar',
    fintech:     'pi pi-bolt',
    dsa:         'pi pi-briefcase',
    broker:      'pi pi-users',
    legal:       'pi pi-shield',
    technical:   'pi pi-wrench',
};

const ROLE_META = {
    super_admin: { label: 'Super admin', icon: 'pi pi-star-fill', severity: 'warning' },
    editor:      { label: 'Editor',      icon: 'pi pi-pencil',    severity: 'info'    },
    viewer:      { label: 'Viewer',      icon: 'pi pi-eye',       severity: 'secondary' },
};

// Maps group name → entity types in that group
const ENTITY_GROUP_KEYS = {
    lenders:  ['bank', 'public_bank', 'nbfc', 'fintech'],
    partners: ['dsa', 'broker'],
    services: ['legal', 'technical'],
};

// Feature cards — Banks first, DSAs second, Experts & Services third
// Feature cards are now pale tinted panels with a coloured heading, chip row
// and solid CTA — `accent` drives badge/title/button/chips so each panel is one
// colour family, and `tint` is the card's wash.
const FEATURE_CARDS = [
    {
        accent:   '#1d4ed8',
        tint:     'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        icon:     'pi pi-building',
        badge:    'LENDERS',
        title:    'Banks & NBFCs',
        subtitle: 'Compare interest rates from top banks, NBFCs and fintechs',
        chips:    ['Home', 'Personal', 'Business', 'Education'],
        group:    'lenders',
    },
    {
        accent:   '#15803d',
        tint:     'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        icon:     'pi pi-briefcase',
        badge:    'PARTNERS',
        title:    'DSAs & Brokers',
        subtitle: 'Work with certified loan distribution agents and brokers',
        chips:    ['Exclusive offers', 'Best rates', 'Trusted network'],
        group:    'partners',
    },
    {
        accent:   '#7c3aed',
        tint:     'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
        icon:     'pi pi-users',
        badge:    'SERVICES',
        title:    'Experts & Services',
        subtitle: 'Legal, technical and consulting services for loan processing',
        chips:    ['Legal', 'MODT', 'Valuation', 'Tech Due Diligence'],
        group:    'services',
    },
];

// Reassurance strip under the feature cards
const TRUST_STRIP = [
    { icon: 'pi pi-users',      accent: '#2563eb', tint: '#dbeafe', title: '5000+',       sub: 'Companies'         },
    { icon: 'pi pi-verified',   accent: '#15803d', tint: '#dcfce7', title: 'Trusted',     sub: 'Verified Partners' },
    { icon: 'pi pi-percentage', accent: '#b45309', tint: '#fef3c7', title: 'Best Rates',  sub: 'Compare & Save'    },
    { icon: 'pi pi-headphones', accent: '#be123c', tint: '#ffe4e6', title: '24/7 Support', sub: "We're here to help" },
];

// Loan product circles — PrimeIcons + gradient background.
//
// Was emoji. Emoji render from the OS font, so the same strip looked different
// on Windows, Android and iOS, sat off the baseline, and carried colours that
// no theme controls. PrimeIcons are the project's only sanctioned icon set —
// one glyph everywhere, sized and coloured by the theme.
// All names verified against the installed primeicons 7.
const LOAN_PRODUCT_CIRCLES = [
    { key: '',                  label: 'All Products',  icon: 'pi-th-large',      bg: 'linear-gradient(135deg, #EEF1FF, #C7D0FF)' },
    { key: 'home_loan',         label: 'Home Loan',     icon: 'pi-home',          bg: 'linear-gradient(135deg, #DBEAFE, #93C5FD)' },
    { key: 'personal_loan',     label: 'Personal Loan', icon: 'pi-credit-card',   bg: 'linear-gradient(135deg, #FEF3C7, #FDE68A)' },
    { key: 'business_loan',     label: 'Business Loan', icon: 'pi-briefcase',     bg: 'linear-gradient(135deg, #D1FAE5, #6EE7B7)' },
    { key: 'education_loan',    label: 'Education',     icon: 'pi-graduation-cap', bg: 'linear-gradient(135deg, #EDE9FE, #C4B5FD)' },
    { key: 'auto_loan',         label: 'Auto Loan',     icon: 'pi-car',           bg: 'linear-gradient(135deg, #FEE2E2, #FCA5A5)' },
    { key: 'gold_loan',         label: 'Gold Loan',     icon: 'pi-star-fill',     bg: 'linear-gradient(135deg, #FEF9C3, #FDE047)' },
    { key: 'agriculture_loan',  label: 'Agriculture',   icon: 'pi-sun',           bg: 'linear-gradient(135deg, #DCFCE7, #86EFAC)' },
    { key: 'construction_loan', label: 'Construction',  icon: 'pi-wrench',        bg: 'linear-gradient(135deg, #FFEDD5, #FED7AA)' },
];

// Secondary entity-type pills
const ENTITY_PILLS = [
    { key: '',           label: 'All'        },
    { key: 'bank',       label: 'Banks'      },
    { key: 'public_bank',label: 'Govt Banks' },
    { key: 'nbfc',       label: 'NBFCs'      },
    { key: 'fintech',    label: 'Fintechs'   },
    { key: 'dsa',        label: 'DSAs'       },
    { key: 'broker',     label: 'Brokers'    },
    { key: 'legal',      label: 'Legal'      },
    { key: 'technical',  label: 'Technical'  },
];

// Horizontal rows in Discovery mode
const ENTITY_GROUPS = [
    { type: 'bank',        title: 'Scheduled Banks',     subtitle: 'Competitive home & business loan rates' },
    { type: 'public_bank', title: 'Government Banks',    subtitle: 'Priority sector & subsidised schemes'   },
    { type: 'nbfc',        title: 'NBFCs',               subtitle: 'Flexible lending for all credit profiles' },
    { type: 'fintech',     title: 'Fintechs & Startups', subtitle: 'Quick disbursals & innovative products'  },
    { type: 'dsa',         title: 'DSAs',                subtitle: 'Certified distribution partners'         },
    { type: 'broker',      title: 'Brokers',             subtitle: 'Connect with experienced loan brokers'   },
    { type: 'legal',       title: 'Legal Services',      subtitle: 'MODT, title search & more'               },
    { type: 'technical',   title: 'Technical Services',  subtitle: 'Property valuation & due diligence'      },
];

const initials = (name) =>
    (name || '').split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?';

/* ─── Inline star display ─────────────────────────────────────────────── */
const StarDisplay = ({ rating, count }) => {
    if (!rating || parseFloat(rating) <= 0) return null;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '0.68rem' }}>
            <span style={{ color: '#f59e0b' }}>★</span>
            <span style={{ fontWeight: 600, color: 'var(--text-color)' }}>{parseFloat(rating).toFixed(1)}</span>
            {count > 0 && <span style={{ color: 'var(--text-color-secondary)' }}>({count})</span>}
        </span>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ═════════════════════════════════════════════════════════════════════ */
const CompanyPagesHub = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    /* ── permissions ──────────────────────────────────────────────────── */
    let userRoles = [];
    try { userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]'); } catch {}
    let primaryRole = '';
    try { primaryRole = JSON.parse(localStorage.getItem('user') || '{}')?.primary_role || ''; } catch {}
    const isImpersonating = localStorage.getItem('isImpersonating') === 'true'
        || localStorage.getItem('isCompanyImpersonating') === 'true';
    const ADMIN_ROLES = ['tenant_admin', 'super_admin', 'company_admin', 'company_owner'];
    const canCreatePage = isImpersonating
        ? ADMIN_ROLES.includes(primaryRole)
        : (ADMIN_ROLES.some((r) => userRoles.includes(r)) || ADMIN_ROLES.includes(primaryRole));

    /* ── state ───────────────────────────────────────────────────────── */
    const [activeTab,       setActiveTab]       = useState(0);
    const [myPages,         setMyPages]         = useState([]);
    const [discoverPages,   setDiscoverPages]   = useState([]);
    const [discoverLoading, setDiscoverLoading] = useState(true);
    const [myLoading,       setMyLoading]       = useState(true);
    const [showCreate,      setShowCreate]      = useState(false);
    const [search,          setSearch]          = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [entityFilter,    setEntityFilter]    = useState('');
    const [followBusy,      setFollowBusy]      = useState({});
    // Product & group filters
    const [loanTypeFilter,  setLoanTypeFilter]  = useState('');
    const [activeGroup,     setActiveGroup]     = useState('');
    const [loanTypePages,   setLoanTypePages]   = useState([]);
    const [loanTypeLoading, setLoanTypeLoading] = useState(false);

    /* ── agent search (unified hero) ─────────────────────────────────────
       The hero search box finds companies AND agents. Company matching is
       local (filteredPages); agents come from the same endpoint the "Find
       Agents" tab uses — /leadflow/network/search — matched by name/email
       via the `q` param. Results render below the companies while searching. */
    const [agentResults,  setAgentResults]  = useState([]);
    const [agentLoading,  setAgentLoading]  = useState(false);
    const [agentSearched, setAgentSearched] = useState(false);
    const [connectingId,  setConnectingId]  = useState(null);
    const [connectedIds,  setConnectedIds]  = useState(new Set());

    // An agent search is active when the hero text is set.
    const agentActive = !!debouncedSearch;

    /* ── debounce search ─────────────────────────────────────────────── */
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 500);
        return () => clearTimeout(t);
    }, [search]);

    /* Fetch agents whenever the debounced query changes. A blank query clears
       the agent results so the section disappears with the rest of the search. */
    useEffect(() => {
        const q = debouncedSearch;
        if (!q) {
            setAgentResults([]);
            setAgentSearched(false);
            setAgentLoading(false);
            return;
        }

        let cancelled = false;
        setAgentLoading(true);
        const params = new URLSearchParams({ q, limit: '12', page: '1' });
        axiosInstance.get(`/leadflow/network/search?${params.toString()}`)
            .then((res) => {
                if (cancelled) return;
                const rows = res.data?.success ? (res.data.data?.results || []) : [];
                setAgentResults(rows);
            })
            .catch(() => { if (!cancelled) setAgentResults([]); })
            .finally(() => { if (!cancelled) { setAgentLoading(false); setAgentSearched(true); } });

        return () => { cancelled = true; };
    }, [debouncedSearch]);

    const handleConnectAgent = async (person) => {
        if (connectingId) return;
        setConnectingId(person.id);
        try {
            const res = await ConnectionService.sendRequest({
                receiver_profile_id: parseInt(person.id, 10),
                connection_message: null,
            });
            if (res.success) {
                setConnectedIds((prev) => new Set([...prev, person.id]));
                toast.current?.show({ severity: 'success', summary: 'Request sent', detail: `Connection request sent to ${person.full_name}`, life: 3000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Failed to send request', life: 3000 });
            }
        } catch (err) {
            const msg = err.response?.data?.message || '';
            if (msg.toLowerCase().includes('already') || err.response?.status === 409 || err.response?.status === 422) {
                setConnectedIds((prev) => new Set([...prev, person.id]));
                toast.current?.show({ severity: 'info', summary: 'Already connected', detail: 'You already have a connection or pending request with this person.', life: 3000 });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to send connection request', life: 3000 });
            }
        } finally {
            setConnectingId(null);
        }
    };

    /* ── loaders ─────────────────────────────────────────────────────── */
    const loadMyPages = useCallback(async () => {
        setMyLoading(true);
        try {
            const res = await companyPagesBusinessService.getMyPages();
            setMyPages(res?.success ? (res.data || []) : []);
        } catch {
            setMyPages([]);
        } finally {
            setMyLoading(false);
        }
    }, []);

    const loadDiscoverPages = useCallback(async () => {
        setDiscoverLoading(true);
        try {
            const res = await companyPagesBusinessService.getPages({ perPage: 100, page: 1 });
            if (res?.success) {
                const payload = res.data;
                const rows = Array.isArray(payload) ? payload : (payload?.data || []);
                setDiscoverPages(rows);
            } else {
                setDiscoverPages([]);
            }
        } catch {
            setDiscoverPages([]);
        } finally {
            setDiscoverLoading(false);
        }
    }, []);

    useEffect(() => {
        loadMyPages();
        loadDiscoverPages();
    }, [loadMyPages, loadDiscoverPages]);

    /* ── loan type pages (server-side filtered) ──────────────────────── */
    useEffect(() => {
        if (!loanTypeFilter) { setLoanTypePages([]); return; }
        setLoanTypeLoading(true);
        companyPagesBusinessService
            .getPages({ loan_type: loanTypeFilter, perPage: 50, page: 1 })
            .then((res) => {
                const rows = res?.success
                    ? (Array.isArray(res.data) ? res.data : (res.data?.data || []))
                    : [];
                setLoanTypePages(rows);
            })
            .catch(() => setLoanTypePages([]))
            .finally(() => setLoanTypeLoading(false));
    }, [loanTypeFilter]);

    /* ── view mode computation ───────────────────────────────────────── */
    const isDiscovery   = !loanTypeFilter && !activeGroup && !entityFilter && !debouncedSearch;
    const isHScrollMode = !!(loanTypeFilter || activeGroup) && !entityFilter && !debouncedSearch;
    // GridMode = !isDiscovery && !isHScrollMode (entityFilter or debouncedSearch is set)

    /* ── derived data ────────────────────────────────────────────────── */
    const managedPages = useMemo(
        () => myPages.filter((p) => ['super_admin', 'admin', 'editor'].includes(p.my_role)),
        [myPages],
    );

    const filteredPages = useMemo(() => {
        let pages = discoverPages;
        if (debouncedSearch) {
            const q = debouncedSearch.toLowerCase();
            pages = pages.filter((p) =>
                (p.name || '').toLowerCase().includes(q) ||
                (p.tagline || '').toLowerCase().includes(q) ||
                (p.hq_city || '').toLowerCase().includes(q),
            );
        }
        if (entityFilter) {
            pages = pages.filter((p) => p.entity_type === entityFilter);
        }
        return pages;
    }, [discoverPages, debouncedSearch, entityFilter]);

    const groupedPages = useMemo(() => {
        const result = {};
        discoverPages.forEach((p) => {
            const type = p.entity_type || 'default';
            if (!result[type]) result[type] = [];
            result[type].push(p);
        });
        return result;
    }, [discoverPages]);

    /* ── filter handlers ─────────────────────────────────────────────── */
    const handleSelectLoanType = (key) => {
        setLoanTypeFilter(key);
        setActiveGroup('');
        setEntityFilter('');
        setSearch('');
    };
    const handleSelectGroup = (group) => {
        setActiveGroup(group || '');
        setLoanTypeFilter('');
        setEntityFilter('');
        setSearch('');
    };
    const handleSelectEntity = (key) => {
        setEntityFilter(key);
        setLoanTypeFilter('');
        setActiveGroup('');
    };
    const handleClearAll = () => {
        setLoanTypeFilter('');
        setActiveGroup('');
        setEntityFilter('');
        setSearch('');
    };

    /* ── follow toggle (optimistic) ──────────────────────────────────── */
    const handleToggleFollow = async (e, page) => {
        e?.stopPropagation?.();
        if (followBusy[page.slug]) return;
        const prev = !!page.is_following;
        const flip = (rows) => rows.map((r) =>
            r.slug === page.slug
                ? { ...r, is_following: !prev, follower_count: Math.max(0, (r.follower_count || 0) + (prev ? -1 : 1)) }
                : r,
        );
        setDiscoverPages((rows) => flip(rows));
        setMyPages((rows) => flip(rows));
        setLoanTypePages((rows) => flip(rows));
        setFollowBusy((s) => ({ ...s, [page.slug]: true }));
        try {
            const res = await companyPagesBusinessService.toggleFollow(page.slug);
            if (res?.success === false) throw new Error(res?.message || 'Failed');
        } catch (err) {
            const rollback = (rows) => rows.map((r) =>
                r.slug === page.slug
                    ? { ...r, is_following: prev, follower_count: Math.max(0, (r.follower_count || 0) + (prev ? 1 : -1)) }
                    : r,
            );
            setDiscoverPages((rows) => rollback(rows));
            setMyPages((rows) => rollback(rows));
            setLoanTypePages((rows) => rollback(rows));
            toast.current?.show({
                severity: 'error',
                summary: prev ? 'Could not unfollow' : 'Could not follow',
                detail: err?.message || 'Try again later',
                life: 3500,
            });
        } finally {
            setFollowBusy((s) => { const next = { ...s }; delete next[page.slug]; return next; });
        }
    };

    /* ── delete ──────────────────────────────────────────────────────── */
    const handleDelete = (e, page) => {
        e?.stopPropagation?.();
        confirmDialog({
            message: `Permanently delete "${page.name}"? This cannot be undone.`,
            header: 'Delete company page',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                const res = await companyPagesBusinessService.deletePage(page.slug);
                if (res?.success !== false) {
                    setMyPages((prev) => prev.filter((p) => p.slug !== page.slug));
                    setDiscoverPages((prev) => prev.filter((p) => p.slug !== page.slug));
                    toast.current?.show({ severity: 'success', summary: 'Deleted', detail: `"${page.name}" deleted.`, life: 3500 });
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Failed', detail: res?.message || 'Could not delete.', life: 4000 });
                }
            },
        });
    };

    const handlePageCreated = (page) => {
        toast.current?.show({ severity: 'success', summary: 'Page created', detail: `${page.name} is now live!`, life: 4000 });
        loadMyPages();
        loadDiscoverPages();
    };

    /* ── render ──────────────────────────────────────────────────────── */
    return (
        <div style={{ marginBottom: '2rem' }}>
            <style>{lcardStyles}</style>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Page header */}
            <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3 mb-4">
                <div>
                    <h1 className="text-xl font-semibold text-900 m-0 mb-1">For Business</h1>
                    <p className="text-600 text-sm m-0">Discover the lending network — banks, NBFCs, fintechs and more</p>
                </div>
                {canCreatePage && (
                    <Button label="Create Page" icon="pi pi-plus" size="small" onClick={() => setShowCreate(true)} className="w-full md:w-auto" />
                )}
            </div>

            <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>

                {/* ── TAB 1: Discover ──────────────────────────────── */}
                <TabPanel header="Discover" leftIcon="pi pi-compass mr-2">

                    {/* Hero search bar */}
                    <HeroBanner
                        count={discoverPages.length}
                        search={search}
                        hasFilters={!isDiscovery}
                        onSearchChange={(v) => {
                            setSearch(v);
                            if (v) { setLoanTypeFilter(''); setActiveGroup(''); }
                        }}
                        onClear={handleClearAll}
                    />

                    {/* Agent results — visible whenever the hero search is set */}
                    {agentActive && (
                        <AgentResultsSection
                            query={debouncedSearch}
                            results={agentResults}
                            loading={agentLoading}
                            searched={agentSearched}
                            connectingId={connectingId}
                            connectedIds={connectedIds}
                            onConnect={handleConnectAgent}
                            onView={(id) => navigate(`/app/profile/${id}`)}
                            onMessage={(id) => navigate(`/app/messaging?profileId=${id}`)}
                        />
                    )}

                    {discoverLoading ? (
                        <DiscoverySkeleton />
                    ) : isDiscovery ? (
                        <DiscoveryView
                            discoverPages={discoverPages}
                            groupedPages={groupedPages}
                            followBusy={followBusy}
                            canCreatePage={canCreatePage}
                            onSelectLoanType={handleSelectLoanType}
                            onSelectGroup={handleSelectGroup}
                            onSelectEntity={handleSelectEntity}
                            onOpen={(slug) => navigate(`/app/business/pages/${slug}`)}
                            onToggleFollow={handleToggleFollow}
                            onCreatePage={() => setShowCreate(true)}
                        />
                    ) : isHScrollMode ? (
                        <HScrollView
                            loanTypeFilter={loanTypeFilter}
                            activeGroup={activeGroup}
                            loanTypePages={loanTypePages}
                            loanTypeLoading={loanTypeLoading}
                            groupedPages={groupedPages}
                            followBusy={followBusy}
                            onSelectLoanType={handleSelectLoanType}
                            onSelectEntity={handleSelectEntity}
                            onClearAll={handleClearAll}
                            onOpen={(slug) => navigate(`/app/business/pages/${slug}`)}
                            onToggleFollow={handleToggleFollow}
                        />
                    ) : (
                        <FilterView
                            filteredPages={filteredPages}
                            debouncedSearch={debouncedSearch}
                            entityFilter={entityFilter}
                            followBusy={followBusy}
                            onSelectLoanType={handleSelectLoanType}
                            onSelectEntity={handleSelectEntity}
                            onClearFilters={handleClearAll}
                            onOpen={(slug) => navigate(`/app/business/pages/${slug}`)}
                            onToggleFollow={handleToggleFollow}
                        />
                    )}
                </TabPanel>

                {/* ── TAB 2: My Companies ──────────────────────────── */}
                <TabPanel
                    header={(
                        <span className="flex align-items-center gap-2">
                            My Companies
                            {managedPages.length > 0 && <Badge value={managedPages.length} severity="secondary" />}
                        </span>
                    )}
                    leftIcon="pi pi-shield mr-2"
                >
                    <div className="surface-ground p-3 border-round mb-3 flex align-items-center justify-content-between">
                        <span className="text-600 text-sm">
                            <i className="pi pi-info-circle mr-2" />
                            Pages you manage as admin or editor
                        </span>
                        {canCreatePage && (
                            <Button label="Create Page" icon="pi pi-plus" size="small" onClick={() => setShowCreate(true)} />
                        )}
                    </div>
                    {myLoading ? (
                        <div className="grid">
                            {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
                        </div>
                    ) : managedPages.length === 0 ? (
                        <ManagedEmptyState canCreate={canCreatePage} onCreate={() => setShowCreate(true)} />
                    ) : (
                        <div className="grid">
                            {managedPages.map((p) => (
                                <ManagedCard
                                    key={p.slug}
                                    page={p}
                                    onOpen={() => navigate(`/app/business/pages/${p.slug}`)}
                                    onDelete={(e) => handleDelete(e, p)}
                                />
                            ))}
                        </div>
                    )}
                </TabPanel>

                {/* ── TAB 3: Find Agents ───────────────────────────────
                    Search people in the Lending Network by name, state,
                    bank/NBFC or loan product. Each result links to the
                    agent's profile (and their posts). */}
                <TabPanel header="Find Agents" leftIcon="pi pi-users mr-2">
                    <NetworkSearch />
                </TabPanel>
            </TabView>

            <CreateCompanyPageDialog
                visible={showCreate}
                onHide={() => setShowCreate(false)}
                onCreated={handlePageCreated}
            />
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
 * HERO BANNER
 * ═════════════════════════════════════════════════════════════════════ */
const HeroBanner = ({ count, search, hasFilters, onSearchChange, onClear }) => (
    <div className="hub-hero">
        {/* Decorative skyline. Purely ornamental, so it's aria-hidden and drops
            out below md where the copy needs the full width. */}
        <svg className="hub-hero-art" viewBox="0 0 340 210" aria-hidden="true">
            <circle cx="255" cy="52" r="76" fill="none" stroke="rgba(255,255,255,.10)" strokeWidth="1" />
            <circle cx="300" cy="34" r="15" fill="rgba(255,255,255,.10)" />
            <circle cx="196" cy="20" r="3"  fill="rgba(255,255,255,.18)" />
            <rect x="118" y="104" width="30" height="106" rx="4" fill="rgba(255,255,255,.13)" />
            <rect x="154" y="66"  width="35" height="144" rx="4" fill="rgba(255,255,255,.20)" />
            <rect x="195" y="92"  width="29" height="118" rx="4" fill="rgba(255,255,255,.14)" />
            <rect x="230" y="120" width="26" height="90"  rx="4" fill="rgba(255,255,255,.10)" />
            {[0, 1, 2, 3].map((r) => [0, 1].map((c) => (
                <rect key={`${r}-${c}`} x={161 + c * 13} y={80 + r * 22} width="8" height="12" rx="1.5" fill="rgba(255,255,255,.30)" />
            )))}
            <circle cx="286" cy="152" r="36" fill="rgba(255,255,255,.13)" />
            <circle cx="286" cy="152" r="36" fill="none" stroke="rgba(255,255,255,.20)" strokeWidth="1" />
        </svg>

        <div className="hub-hero-copy">
            <h2 className="hub-hero-title">
                Explore the Lending Network
                <span className="hub-hero-spark" aria-hidden="true">✦</span>
            </h2>
            <p className="hub-hero-sub">
                {count > 0
                    ? `${count} companies across banks, NBFCs, fintechs & more`
                    : 'Find banks, NBFCs, fintechs, and certified partners'}
            </p>

            <div className="hub-hero-search">
                <IconField iconPosition="left" style={{ flex: 1 }}>
                    <InputIcon className="pi pi-search" style={{ color: 'var(--text-color-secondary)' }} />
                    <InputText
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search companies, agents, cities…"
                        className="w-full"
                    />
                </IconField>
                {(search || hasFilters) && (
                    <Button
                        icon="pi pi-times"
                        onClick={onClear}
                        tooltip="Clear filters"
                        style={{ borderRadius: '12px', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.28)', color: 'white', flexShrink: 0 }}
                    />
                )}
            </div>
        </div>
    </div>
);

/* ── Trust strip ─────────────────────────────────────────────────────── */
const TrustStrip = () => (
    <div className="hub-trust">
        {TRUST_STRIP.map((t) => (
            <div key={t.title} className="hub-trust-item">
                <span className="hub-trust-icon" style={{ background: t.tint, color: t.accent }}>
                    <i className={t.icon} aria-hidden="true" />
                </span>
                <span className="min-w-0">
                    <span className="hub-trust-title">{t.title}</span>
                    <span className="hub-trust-sub">{t.sub}</span>
                </span>
            </div>
        ))}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * LOAN PRODUCT CIRCLES (PrimeIcon + gradient, prototype style)
 * ═════════════════════════════════════════════════════════════════════ */
const LoanProductCircles = ({ active, onSelect }) => (
    <div className="hub-rail">
        <div className="hub-rail-track">
            {LOAN_PRODUCT_CIRCLES.map(({ key, label, icon, bg }) => {
                const isActive = key === active;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => onSelect(key)}
                        aria-pressed={isActive}
                        className={`hub-rail-item${isActive ? ' is-active' : ''}`}
                    >
                        {/* Active swaps the pastel fill for a white disc with a
                            primary ring, so it reads as selected even at a glance. */}
                        <span
                            className="hub-rail-circle"
                            style={isActive ? undefined : { background: bg }}
                        >
                            {/* One icon colour for the whole strip rather than a
                                per-category accent: nine hand-picked colours on
                                nine pastel gradients is a contrast problem to
                                re-check on every theme change. The selected one
                                turns primary, which is what carries the state. */}
                            <i
                                className={`pi ${icon} ${isActive ? 'text-primary' : 'text-900'}`}
                                aria-hidden="true"
                            />
                        </span>
                        <span className="hub-rail-label">{label}</span>
                    </button>
                );
            })}
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * ENTITY PILLS (secondary filter bar)
 * ═════════════════════════════════════════════════════════════════════ */
const EntityPills = ({ active, onSelect }) => (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {ENTITY_PILLS.map(({ key, label }) => {
            const isActive = key === active;
            return (
                <button
                    key={key}
                    onClick={() => onSelect(key)}
                    style={{
                        padding: '4px 14px',
                        borderRadius: '20px',
                        border: `1.5px solid ${isActive ? 'var(--primary-color)' : 'var(--surface-border)'}`,
                        background: isActive ? 'var(--primary-color)' : 'var(--surface-card)',
                        color: isActive ? 'white' : 'var(--text-color-secondary)',
                        fontSize: '0.75rem',
                        fontWeight: isActive ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </button>
            );
        })}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * DISCOVERY MODE
 * ═════════════════════════════════════════════════════════════════════ */
/* ── Viewport helper ─────────────────────────────────────────────────── */
const useIsMobile = (query = '(max-width: 767px)') => {
    const [matches, setMatches] = useState(
        () => typeof window !== 'undefined' && window.matchMedia(query).matches
    );
    useEffect(() => {
        const mq = window.matchMedia(query);
        const onChange = (e) => setMatches(e.matches);
        setMatches(mq.matches);
        // addEventListener isn't available on MediaQueryList in older WebKit
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, [query]);
    return matches;
};

/* ── Shared company row ──────────────────────────────────────────────────
   Desktop keeps the horizontal scroll strip. On a phone that strip clipped
   the second card mid-way with no scroll affordance, so below 768px the row
   shows ONE centred card and "See all" expands it into a vertical stack of
   the same cards in place, rather than navigating away to the filter view.  */
const PageRow = ({ pages, limit = 10, headerLeft, onSeeAll, followBusy, onOpen, onToggleFollow }) => {
    const isMobile = useIsMobile();
    const [expanded, setExpanded] = useState(false);

    const visible  = pages.slice(0, limit);
    const shown    = isMobile && !expanded ? visible.slice(0, 1) : visible;
    const canToggle = isMobile && visible.length > 1;
    const showButton = canToggle || !!onSeeAll;

    const cardProps = (p) => ({
        page: p,
        followBusy: !!followBusy[p.slug],
        onOpen: () => onOpen(p.slug),
        onToggleFollow: (e) => onToggleFollow(e, p),
    });

    return (
        <>
            {(headerLeft || showButton) && (
                <div className="flex align-items-center justify-content-between gap-2 mb-2">
                    <div className="flex align-items-center gap-2 min-w-0">{headerLeft}</div>
                    {showButton && (
                        <Button
                            label={canToggle ? (expanded ? 'Show less' : `See all (${pages.length})`) : 'See all'}
                            icon={canToggle ? (expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down') : 'pi pi-arrow-right'}
                            iconPos="right"
                            text
                            size="small"
                            onClick={() => (canToggle ? setExpanded((v) => !v) : onSeeAll?.())}
                            style={{ fontSize: '0.8rem', flexShrink: 0 }}
                        />
                    )}
                </div>
            )}

            {isMobile ? (
                <div className="flex flex-column align-items-center" style={{ gap: '14px' }}>
                    {shown.map((p) => (
                        <div key={p.slug} style={{ width: '100%', maxWidth: '320px' }}>
                            <LCard stacked {...cardProps(p)} />
                        </div>
                    ))}
                    {expanded && pages.length > limit && (
                        <div style={{ width: '100%', maxWidth: '320px' }}>
                            <SeeMoreCard label={`+${pages.length - limit} more`} onClick={onSeeAll} />
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', gap: '14px', overflowX: 'auto', paddingBottom: '8px', scrollbarWidth: 'thin' }}>
                    {visible.map((p) => <LCard key={p.slug} {...cardProps(p)} />)}
                    {pages.length > limit && (
                        <SeeMoreCard label={`+${pages.length - limit} more`} onClick={onSeeAll} />
                    )}
                </div>
            )}
        </>
    );
};

const DiscoveryView = ({ discoverPages, groupedPages, followBusy, canCreatePage, onSelectLoanType, onSelectGroup, onSelectEntity, onOpen, onToggleFollow, onCreatePage }) => (
    <div>
        {/* Loan product circles */}
        <LoanProductCircles active="" onSelect={(key) => { if (key) onSelectLoanType(key); }} />

        {/* 3 Feature entry cards — Banks, DSAs, Experts */}
        <div className="grid mb-3">
            {FEATURE_CARDS.map((fc) => (
                <div key={fc.group} className="col-12 md:col-4">
                    <FeatureSCard {...fc} onFilter={() => onSelectGroup(fc.group)} />
                </div>
            ))}
        </div>

        <TrustStrip />

        {/* Horizontal scroll rows by entity type */}
        {ENTITY_GROUPS.map(({ type, title, subtitle }) => {
            const pages = groupedPages[type] || [];
            if (pages.length === 0) return null;
            return (
                <div key={type} className="mb-5">
                    <PageRow
                        pages={pages}
                        followBusy={followBusy}
                        onOpen={onOpen}
                        onToggleFollow={onToggleFollow}
                        onSeeAll={() => onSelectEntity(type)}
                        headerLeft={(
                            <>
                                <i className={ENTITY_ICON[type]} style={{ color: 'var(--primary-color)', fontSize: '1rem' }} />
                                <span className="text-900 font-semibold" style={{ fontSize: '1rem' }}>{title}</span>
                                {subtitle && <span className="text-500 text-xs hidden md:inline">{subtitle}</span>}
                            </>
                        )}
                    />
                </div>
            );
        })}

        {discoverPages.length === 0 && (
            <div className="flex flex-column align-items-center text-center py-8 gap-3">
                <div style={{ width: '5rem', height: '5rem', borderRadius: '50%', background: 'var(--surface-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="pi pi-building" style={{ fontSize: '2rem', color: 'var(--surface-400)' }} />
                </div>
                <div>
                    <div className="text-900 font-semibold text-lg">No company pages yet</div>
                    <p className="text-600 text-sm m-0 mt-1">Check back soon as more companies join the network.</p>
                </div>
                {canCreatePage && (
                    <Button label="Create the first page" icon="pi pi-plus" size="small" onClick={onCreatePage} />
                )}
            </div>
        )}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * HSCROLL MODE — loan type or group filter active
 * ═════════════════════════════════════════════════════════════════════ */
const HScrollView = ({ loanTypeFilter, activeGroup, loanTypePages, loanTypeLoading, groupedPages, followBusy, onSelectLoanType, onSelectEntity, onClearAll, onOpen, onToggleFollow }) => {
    const activeCircle  = LOAN_PRODUCT_CIRCLES.find((c) => c.key === loanTypeFilter);
    const activeGroupFC = FEATURE_CARDS.find((fc) => fc.group === activeGroup);
    const groupTypes    = activeGroup ? (ENTITY_GROUP_KEYS[activeGroup] || []) : [];

    return (
        <div>
            {/* Loan product circles (with active state) */}
            <LoanProductCircles active={loanTypeFilter} onSelect={onSelectLoanType} />

            {/* Entity pills (clicking drills into GridMode) */}
            <EntityPills active="" onSelect={(key) => key ? onSelectEntity(key) : onClearAll()} />

            {/* Active filter chip */}
            <div className="flex align-items-center gap-2 mb-3">
                <span
                    className="px-3 py-1 border-round-xl text-sm font-medium flex align-items-center gap-1"
                    style={{ background: 'var(--primary-100)', color: 'var(--primary-700)', border: '1px solid var(--primary-200)' }}
                >
                    {loanTypeFilter && activeCircle && (
                        <><i className={`pi ${activeCircle.icon}`} style={{ fontSize: '0.75rem' }} aria-hidden="true" /> {activeCircle.label}</>
                    )}
                    {activeGroup && activeGroupFC && (
                        <><i className={activeGroupFC.icon} style={{ fontSize: '0.75rem' }} /> {activeGroupFC.title}</>
                    )}
                    <button
                        onClick={onClearAll}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 0 4px', color: 'var(--primary-700)', fontSize: '1rem', lineHeight: 1 }}
                    >×</button>
                </span>
            </div>

            {/* Content: single row (loan type) or multi-row (group) */}
            {loanTypeFilter ? (
                loanTypeLoading ? (
                    <div style={{ display: 'flex', gap: '14px', overflow: 'hidden' }}>
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} width="220px" height="230px" style={{ flexShrink: 0, borderRadius: '12px' }} />
                        ))}
                    </div>
                ) : loanTypePages.length === 0 ? (
                    <div className="flex flex-column align-items-center text-center py-7 gap-3">
                        <i className="pi pi-search text-5xl" style={{ color: 'var(--surface-300)' }} />
                        <div>
                            <div className="text-900 font-semibold">No companies found for this product</div>
                            <p className="text-600 text-sm m-0 mt-1">Try a different loan type or browse all.</p>
                        </div>
                        <Button label="View all companies" icon="pi pi-building" size="small" outlined onClick={onClearAll} />
                    </div>
                ) : (
                    <div>
                        <PageRow
                            pages={loanTypePages}
                            limit={loanTypePages.length}
                            followBusy={followBusy}
                            onOpen={onOpen}
                            onToggleFollow={onToggleFollow}
                            headerLeft={(
                                <span className="text-900 font-semibold" style={{ fontSize: '0.95rem' }}>
                                    {loanTypePages.length} {loanTypePages.length === 1 ? 'company' : 'companies'} offering {activeCircle?.label}
                                </span>
                            )}
                        />
                    </div>
                )
            ) : activeGroup ? (
                <div>
                    {groupTypes.map((type) => {
                        const pages = groupedPages[type] || [];
                        const groupInfo = ENTITY_GROUPS.find((g) => g.type === type);
                        if (pages.length === 0) return null;
                        return (
                            <div key={type} className="mb-5">
                                <PageRow
                                    pages={pages}
                                    followBusy={followBusy}
                                    onOpen={onOpen}
                                    onToggleFollow={onToggleFollow}
                                    onSeeAll={() => onSelectEntity(type)}
                                    headerLeft={(
                                        <>
                                            <i className={ENTITY_ICON[type]} style={{ color: 'var(--primary-color)', fontSize: '1rem' }} />
                                            <span className="text-900 font-semibold" style={{ fontSize: '1rem' }}>{groupInfo?.title || ENTITY_LABEL[type]}</span>
                                            {groupInfo?.subtitle && <span className="text-500 text-xs hidden md:inline">{groupInfo.subtitle}</span>}
                                        </>
                                    )}
                                />
                            </div>
                        );
                    })}
                    {groupTypes.every((type) => (groupedPages[type] || []).length === 0) && (
                        <div className="flex flex-column align-items-center text-center py-7 gap-3">
                            <i className="pi pi-building text-5xl" style={{ color: 'var(--surface-300)' }} />
                            <div className="text-900 font-semibold">No companies in this category yet</div>
                            <Button label="Back to all" icon="pi pi-arrow-left" size="small" outlined onClick={onClearAll} />
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
 * FILTER / GRID MODE — entity pill or search
 * ═════════════════════════════════════════════════════════════════════ */
const FilterView = ({ filteredPages, debouncedSearch, entityFilter, followBusy, onSelectLoanType, onSelectEntity, onClearFilters, onOpen, onToggleFollow }) => (
    <div>
        {/* Loan product circles */}
        <LoanProductCircles active="" onSelect={(key) => { if (key) onSelectLoanType(key); }} />

        {/* Entity pills */}
        <EntityPills active={entityFilter} onSelect={onSelectEntity} />

        {/* Active filter chips + result count */}
        <div className="flex align-items-center gap-2 mb-3 flex-wrap">
            {entityFilter && (
                <span
                    className="flex align-items-center gap-1 px-3 py-1 border-round-xl text-sm font-medium cursor-pointer"
                    style={{ background: 'var(--primary-100)', color: 'var(--primary-700)', border: '1px solid var(--primary-200)' }}
                    onClick={() => onSelectEntity('')}
                >
                    <i className={ENTITY_ICON[entityFilter]} style={{ fontSize: '0.75rem' }} />
                    {ENTITY_LABEL[entityFilter]}
                    <i className="pi pi-times ml-1" style={{ fontSize: '0.6rem' }} />
                </span>
            )}
            <span className="text-sm text-500">
                {filteredPages.length} result{filteredPages.length !== 1 ? 's' : ''}
                {debouncedSearch ? <> for "<strong className="text-700">{debouncedSearch}</strong>"</> : null}
            </span>
        </div>

        {filteredPages.length === 0 ? (
            <div className="flex flex-column align-items-center text-center py-7 gap-3">
                <i className="pi pi-search text-5xl" style={{ color: 'var(--surface-300)' }} />
                <div>
                    <div className="text-900 font-semibold">No companies found</div>
                    <p className="text-600 text-sm m-0 mt-1">Try a different search or browse all categories.</p>
                </div>
                <Button label="Clear filters" icon="pi pi-filter-slash" size="small" outlined severity="secondary" onClick={onClearFilters} />
            </div>
        ) : (
            <div className="grid">
                {filteredPages.map((p) => (
                    <LCard
                        key={p.slug}
                        page={p}
                        followBusy={!!followBusy[p.slug]}
                        onOpen={() => onOpen(p.slug)}
                        onToggleFollow={(e) => onToggleFollow(e, p)}
                        gridMode
                    />
                ))}
            </div>
        )}
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * AGENT RESULTS — agents matching the hero search (name / email), shown
 * under the hero so one place surfaces both companies and agents.
 * ═════════════════════════════════════════════════════════════════════ */
const AgentResultsSection = ({ query, results, loading, searched, connectingId, connectedIds, onConnect, onView, onMessage }) => {
    return (
        <div className="mt-3 mb-3">
            <div className="flex align-items-center gap-2 mb-3">
                <i className="pi pi-users text-primary" />
                <h3 className="text-900 font-semibold m-0 text-base">
                    Agents{!loading && searched ? <span className="text-500 font-normal"> · {results.length}</span> : null}
                </h3>
                {query ? <span className="text-500 text-sm">for "<strong className="text-700">{query}</strong>"</span> : null}
            </div>

            {!loading && searched && results.length === 0 ? (
                <div className="flex flex-column align-items-center text-center py-5 gap-2 surface-card border-round-xl border-1 surface-border">
                    <i className="pi pi-users text-4xl" style={{ color: 'var(--surface-300)' }} />
                    <div className="text-900 font-semibold">No agents found</div>
                    <p className="text-600 text-sm m-0">Try a different name/email, bank, state, or loan product.</p>
                </div>
            ) : loading ? (
                <div className="grid">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="col-12 md:col-6 lg:col-4">
                            <div className="border-1 surface-border border-round p-3">
                                <div className="flex gap-3 align-items-start mb-3">
                                    <Skeleton shape="circle" size="3rem" />
                                    <div className="flex-1">
                                        <Skeleton className="mb-2" height="1rem" width="70%" />
                                        <Skeleton height="0.8rem" width="90%" />
                                    </div>
                                </div>
                                <Skeleton height="2rem" borderRadius="6px" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid">
                    {results.map((person) => {
                        const isConnecting  = connectingId === person.id;
                        const isConnected   = person.connection_status === 'connected';
                        const requestSent   = connectedIds.has(person.id) || person.connection_status === 'pending_sent';
                        const requestRecv   = person.connection_status === 'pending_received';
                        const location      = [person.location_city, person.location_state].filter(Boolean).join(', ');

                        return (
                            <div key={person.id} className="col-12 md:col-6 lg:col-4">
                                <div className="border-1 surface-border border-round p-3 h-full flex flex-column justify-content-between hover:shadow-2 transition-shadow transition-duration-200">
                                    <div>
                                        <div className="flex gap-3 align-items-start mb-3">
                                            <Avatar
                                                image={person.profile_photo_url}
                                                label={person.full_name?.charAt(0)?.toUpperCase()}
                                                shape="circle"
                                                size="large"
                                                className="flex-shrink-0 cursor-pointer"
                                                onClick={() => onView(person.id)}
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div
                                                    className="font-semibold text-900 text-sm cursor-pointer hover:text-primary flex align-items-center gap-1"
                                                    onClick={() => onView(person.id)}
                                                >
                                                    {person.full_name}
                                                    {person.is_verified && (
                                                        <i className="pi pi-verified text-primary" style={{ fontSize: '0.8rem' }} title="Verified" />
                                                    )}
                                                </div>
                                                {person.current_designation && (
                                                    <div className="text-xs text-color-secondary mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                        {person.current_designation}
                                                    </div>
                                                )}
                                                {person.current_company && (
                                                    <div className="flex align-items-center gap-1 mt-1">
                                                        <i className="pi pi-building text-xs" style={{ color: 'var(--primary-color)' }} />
                                                        <span className="text-xs font-medium text-primary">{person.current_company}</span>
                                                    </div>
                                                )}
                                                {location && (
                                                    <div className="flex align-items-center gap-1 mt-1">
                                                        <i className="pi pi-map-marker text-xs text-color-secondary" />
                                                        <span className="text-xs text-color-secondary">{location}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            label="View Profile"
                                            icon="pi pi-user"
                                            className="p-button-outlined p-button-sm flex-1"
                                            style={{ fontSize: '0.75rem' }}
                                            onClick={() => onView(person.id)}
                                        />
                                        {isConnected ? (
                                            <Button icon="pi pi-comments" label="Message" className="p-button-sm flex-1" style={{ fontSize: '0.75rem' }} onClick={() => onMessage(person.id)} />
                                        ) : requestRecv ? (
                                            <Button icon="pi pi-user-plus" label="Respond" className="p-button-sm p-button-outlined flex-1" style={{ fontSize: '0.75rem' }} onClick={() => onView(person.id)} />
                                        ) : requestSent ? (
                                            <Button icon="pi pi-check" label="Sent" className="p-button-sm p-button-outlined p-button-secondary flex-1" style={{ fontSize: '0.75rem' }} disabled />
                                        ) : (
                                            <Button
                                                icon={isConnecting ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
                                                label="Connect"
                                                className="p-button-sm flex-1"
                                                style={{ fontSize: '0.75rem' }}
                                                onClick={() => onConnect(person)}
                                                disabled={!!connectingId}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
 * FEATURE SCARD (3 entry cards at top of discovery)
 * ═════════════════════════════════════════════════════════════════════ */
const FeatureSCard = ({ accent, tint, icon, badge, title, subtitle, chips, onFilter }) => (
    <div
        className="hub-feature"
        onClick={onFilter}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onFilter(); } }}
        style={{ background: tint, '--hub-accent': accent }}
    >
        <span className="hub-feature-badge">{badge}</span>
        <h3 className="hub-feature-title">{title}</h3>
        <p className="hub-feature-sub">{subtitle}</p>

        <div className="hub-feature-chips">
            {chips.map((c) => <span key={c} className="hub-feature-chip">{c}</span>)}
        </div>

        <span className="hub-feature-cta">
            Explore <i className="pi pi-arrow-right" aria-hidden="true" />
        </span>

        {/* Oversized watermark icon, clipped by the card's overflow */}
        <i className={`${icon} hub-feature-art`} aria-hidden="true" />
    </div>
);

/* ═══════════════════════════════════════════════════════════════════════
 * LCARD — Swiggy-style company card with star rating
 * gridMode=false → fixed 220px for horizontal scroll
 * gridMode=true  → fills col-* in grid layout
 * ═════════════════════════════════════════════════════════════════════ */
/* ── Company card styling ────────────────────────────────────────────────
   Lives in CSS rather than the inline onMouseEnter/onMouseLeave handlers the
   card used before, so the hover state can choreograph several elements at
   once (lift, banner zoom, logo pop, button shade) instead of only the card's
   own shadow — and so it reverts cleanly if the pointer leaves mid-transition. */
const lcardStyles = `
    .lcard {
        position: relative;
        border-radius: 16px;
        border: 1px solid var(--surface-border);
        background: var(--surface-card);
        cursor: pointer;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-shadow: 0 1px 3px rgba(10, 19, 32, 0.06);
        transition: transform .25s cubic-bezier(.4,0,.2,1),
                    box-shadow .25s cubic-bezier(.4,0,.2,1);
    }
    /* Thin accent rule along the very top, in the entity's colour */
    .lcard::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 3px;
        background: var(--lcard-accent);
        z-index: 3;
    }
    .lcard:hover,
    .lcard:focus-visible {
        transform: translateY(-4px);
        box-shadow: 0 14px 32px rgba(10, 19, 32, 0.13), 0 4px 10px rgba(10, 19, 32, 0.05);
        outline: none;
    }
    /* Light banner — the logo tile and badge carry the colour, so the
       backdrop stays pale and the layered waves give it movement. */
    .lcard-banner {
        height: 96px;
        position: relative;
        overflow: hidden;
        background: linear-gradient(180deg, var(--surface-0) 0%, var(--surface-100) 100%);
    }
    .lcard-wave {
        position: absolute;
        left: 0; right: 0; bottom: -1px;
        width: 100%;
        height: 62px;
        display: block;
    }
    .lcard-logo {
        position: absolute;
        top: 15px; left: 15px;
        z-index: 2;
        width: 48px;
        height: 48px;
        border-radius: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        object-fit: cover;
        color: #fff;
        font-weight: 700;
        font-size: 1.15rem;
        letter-spacing: -0.02em;
        box-shadow: 0 6px 14px rgba(10, 19, 32, 0.20);
        transition: transform .3s cubic-bezier(.34,1.56,.64,1);
    }
    .lcard:hover .lcard-logo { transform: scale(1.07) rotate(-3deg); }
    /* Light pill badge, entity colour as the text rather than the fill */
    .lcard-chip {
        position: absolute;
        top: 17px;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 3px;
        background: var(--surface-0);
        border: 1px solid var(--surface-border);
        color: var(--lcard-accent);
        font-size: 0.58rem;
        font-weight: 700;
        letter-spacing: 0.06em;
        padding: 4px 9px;
        border-radius: 999px;
        box-shadow: 0 1px 3px rgba(10, 19, 32, 0.07);
    }
    .lcard-body {
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 7px;
        flex: 1;
    }
    .lcard-name {
        font-weight: 700;
        font-size: 0.95rem;
        letter-spacing: -0.01em;
        color: var(--text-color);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .lcard-meta {
        font-size: 0.72rem;
        color: var(--text-color-secondary);
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .lcard-tagline {
        background: var(--surface-50);
        border: 1px solid var(--surface-border);
        border-radius: 8px;
        padding: 7px 10px;
        font-size: 0.68rem;
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .lcard-btn {
        width: 100%;
        padding: 9px 0;
        border-radius: 10px;
        font-size: 0.78rem;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        transition: filter .18s ease, box-shadow .18s ease, background .18s ease;
    }
    .lcard-btn-primary {
        border: 1px solid var(--lcard-accent);
        background: var(--lcard-accent);
        color: #fff;
    }
    .lcard:hover .lcard-btn-primary {
        filter: brightness(1.08);
        box-shadow: 0 5px 14px rgba(10, 19, 32, 0.22);
    }
    .lcard-btn-ghost {
        border: 1px solid var(--surface-300);
        background: var(--surface-0);
        color: var(--text-color-secondary);
    }
    .lcard:hover .lcard-btn-ghost { background: var(--surface-100); }
    @media (prefers-reduced-motion: reduce) {
        .lcard, .lcard-logo, .lcard-btn { transition: none; }
        .lcard:hover { transform: none; }
        .lcard:hover .lcard-logo { transform: none; }
    }

    /* ── Hero ─────────────────────────────────────────────────────────── */
    .hub-hero {
        position: relative;
        overflow: hidden;
        border-radius: 18px;
        padding: 2.25rem 1.75rem;
        margin-bottom: 1.25rem;
        background: linear-gradient(115deg, #0b2a86 0%, #13379e 45%, #1d4ed8 100%);
    }
    .hub-hero-art {
        position: absolute;
        right: 0;
        bottom: 0;
        height: 100%;
        width: auto;
        max-width: 45%;
        pointer-events: none;
    }
    .hub-hero-copy { position: relative; max-width: 620px; }
    .hub-hero-title {
        margin: 0 0 0.4rem;
        color: #fff;
        font-size: 1.9rem;
        font-weight: 700;
        letter-spacing: -0.025em;
        line-height: 1.15;
    }
    .hub-hero-spark {
        font-size: 0.85rem;
        margin-left: 0.5rem;
        color: #fcd34d;
        vertical-align: super;
    }
    .hub-hero-sub {
        margin: 0 0 1.35rem;
        color: rgba(255, 255, 255, 0.72);
        font-size: 0.95rem;
    }
    .hub-hero-search { display: flex; gap: 8px; max-width: 560px; }
    /* White pill field — high contrast against the deep blue, unlike the old
       translucent input whose placeholder was barely legible. */
    .hub-hero-search .p-inputtext {
        border-radius: 12px;
        border: none;
        background: #fff;
        color: var(--text-color);
        padding: 0.85rem 1rem 0.85rem 2.75rem;
        font-size: 0.95rem;
        box-shadow: 0 4px 14px rgba(3, 12, 45, 0.18);
    }
    .hub-hero-search .p-inputtext:focus {
        box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.35), 0 4px 14px rgba(3, 12, 45, 0.18);
    }
    .hub-hero-search .p-icon-field > .p-input-icon { left: 1rem; }
    @media screen and (max-width: 767px) {
        .hub-hero { padding: 1.5rem 1.15rem; border-radius: 16px; }
        .hub-hero-title { font-size: 1.35rem; }
        .hub-hero-sub { font-size: 0.85rem; margin-bottom: 1rem; }
        .hub-hero-art { display: none; }
    }

    /* ── Loan product rail ────────────────────────────────────────────── */
    .hub-rail {
        background: var(--surface-card);
        border: 1px solid var(--surface-border);
        border-radius: 16px;
        padding: 1.1rem 0.5rem 0.85rem;
        margin-bottom: 1.25rem;
        overflow-x: auto;
        scrollbar-width: thin;
    }
    .hub-rail-track {
        display: flex;
        gap: 0.5rem;
        min-width: max-content;
        justify-content: center;
    }
    .hub-rail-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        min-width: 88px;
        padding: 0 4px 8px;
        border: none;
        background: none;
        cursor: pointer;
        font-family: inherit;
        position: relative;
    }
    .hub-rail-circle {
        width: 66px;
        height: 66px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
        line-height: 1;
        border: 2px solid transparent;
        transition: transform .22s cubic-bezier(.34,1.56,.64,1), box-shadow .2s ease;
    }
    /* A PrimeIcon fills more of its em box than an emoji does, so it needs to
       sit a couple of px under the circle's own font-size to keep the same
       optical weight the emoji had. */
    .hub-rail-circle .pi { font-size: 24px; }
    .hub-rail-item:hover .hub-rail-circle { transform: translateY(-3px); }
    .hub-rail-item.is-active .hub-rail-circle {
        background: var(--surface-0);
        border-color: var(--primary-color);
        box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.10);
    }
    .hub-rail-label {
        font-size: 0.78rem;
        color: var(--text-color-secondary);
        text-align: center;
        line-height: 1.3;
    }
    .hub-rail-item.is-active .hub-rail-label {
        color: var(--primary-color);
        font-weight: 600;
    }
    /* Underline indicator for the selected product */
    .hub-rail-item.is-active::after {
        content: '';
        position: absolute;
        left: 50%;
        bottom: 0;
        transform: translateX(-50%);
        width: 62px;
        height: 3px;
        border-radius: 2px;
        background: var(--primary-color);
    }
    /* On a phone the 9-item strip is ~790px wide and scrolls sideways behind an
       invisible scrollbar, so half the products are undiscoverable. Wrap into a
       3-column grid instead: 9 items divide evenly into 3 rows, which is the
       same height a 4-column grid would need (4+4+1) but without a ragged last
       row, and it leaves room for larger tap targets. */
    @media screen and (max-width: 767px) {
        .hub-rail {
            overflow-x: visible;
            padding: 1rem 0.35rem 0.6rem;
        }
        .hub-rail-track {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem 0.25rem;
            min-width: 0;
        }
        .hub-rail-item {
            min-width: 0;
            padding: 0 2px 8px;
            align-self: start;
        }
        .hub-rail-circle { width: 60px; height: 60px; font-size: 26px; }
        .hub-rail-circle .pi { font-size: 22px; }
        /* Two lines' worth of height on every label, so a wrapped one
           ("Personal Loan") doesn't shove its underline out of line with
           the single-line items beside it. */
        .hub-rail-label {
            font-size: 0.72rem;
            min-height: 2.1em;
            display: flex;
            align-items: flex-start;
            justify-content: center;
        }
        .hub-rail-item.is-active::after { width: 48px; }
    }

    /* ── Feature cards ────────────────────────────────────────────────── */
    .hub-feature {
        position: relative;
        overflow: hidden;
        height: 100%;
        border-radius: 16px;
        padding: 1.35rem 1.4rem;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        transition: transform .25s cubic-bezier(.4,0,.2,1), box-shadow .25s cubic-bezier(.4,0,.2,1);
    }
    .hub-feature:hover,
    .hub-feature:focus-visible {
        transform: translateY(-4px);
        box-shadow: 0 16px 34px rgba(10, 19, 32, 0.13);
        outline: none;
    }
    .hub-feature-badge {
        align-self: flex-start;
        background: rgba(255, 255, 255, 0.75);
        color: var(--hub-accent);
        border-radius: 999px;
        padding: 4px 11px;
        font-size: 0.62rem;
        font-weight: 800;
        letter-spacing: 0.09em;
        margin-bottom: 0.9rem;
    }
    .hub-feature-title {
        margin: 0 0 0.5rem;
        color: var(--hub-accent);
        font-size: 1.3rem;
        font-weight: 700;
        letter-spacing: -0.02em;
    }
    .hub-feature-sub {
        margin: 0 0 1rem;
        color: var(--text-color-secondary);
        font-size: 0.88rem;
        line-height: 1.5;
        max-width: 22rem;
        position: relative;
    }
    .hub-feature-chips {
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
        margin-bottom: 1.35rem;
        position: relative;
    }
    .hub-feature-chip {
        background: rgba(255, 255, 255, 0.75);
        border: 1px solid rgba(255, 255, 255, 0.9);
        color: var(--hub-accent);
        border-radius: 999px;
        padding: 5px 12px;
        font-size: 0.74rem;
        font-weight: 500;
        white-space: nowrap;
    }
    .hub-feature-cta {
        align-self: flex-start;
        margin-top: auto;
        display: inline-flex;
        align-items: center;
        gap: 9px;
        background: var(--hub-accent);
        color: #fff;
        border-radius: 11px;
        padding: 0.7rem 1.35rem;
        font-size: 0.9rem;
        font-weight: 600;
        position: relative;
        transition: box-shadow .2s ease, filter .2s ease;
    }
    .hub-feature-cta i { font-size: 0.8rem; transition: transform .25s cubic-bezier(.4,0,.2,1); }
    .hub-feature:hover .hub-feature-cta { filter: brightness(1.08); box-shadow: 0 6px 16px rgba(10, 19, 32, 0.22); }
    .hub-feature:hover .hub-feature-cta i { transform: translateX(4px); }
    .hub-feature-art {
        position: absolute;
        right: -14px;
        top: 46%;
        transform: translateY(-50%);
        font-size: 8rem;
        color: var(--hub-accent);
        opacity: 0.10;
        pointer-events: none;
    }

    /* ── Trust strip ──────────────────────────────────────────────────── */
    .hub-trust {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        background: var(--surface-card);
        border: 1px solid var(--surface-border);
        border-radius: 16px;
        padding: 1.1rem 0.5rem;
        margin-bottom: 1.75rem;
    }
    .hub-trust-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 0 1.1rem;
        min-width: 0;
    }
    .hub-trust-item + .hub-trust-item { border-left: 1px solid var(--surface-border); }
    .hub-trust-icon {
        flex-shrink: 0;
        width: 2.6rem;
        height: 2.6rem;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.05rem;
    }
    .hub-trust-title {
        display: block;
        font-weight: 700;
        font-size: 0.95rem;
        color: var(--text-color);
        line-height: 1.25;
    }
    .hub-trust-sub {
        display: block;
        font-size: 0.78rem;
        color: var(--text-color-secondary);
    }
    @media screen and (max-width: 767px) {
        /* 4 across is unreadable on a phone — 2x2, and the vertical divider
           between columns 1 and 3 has to be cleared or it floats mid-row. */
        .hub-trust { grid-template-columns: repeat(2, 1fr); row-gap: 1rem; }
        .hub-trust-item { padding: 0 0.7rem; }
        .hub-trust-item:nth-child(odd) { border-left: none; }
    }
    @media (prefers-reduced-motion: reduce) {
        .hub-feature, .hub-feature-cta, .hub-feature-cta i, .hub-rail-circle { transition: none; }
        .hub-feature:hover { transform: none; }
        .hub-feature:hover .hub-feature-cta i { transform: none; }
        .hub-rail-item:hover .hub-rail-circle { transform: none; }
    }
`;

// gridMode → wrapped in responsive grid columns; stacked → full-width, no
// wrapper (used by PageRow's mobile vertical list, which sizes it itself).
const LCard = ({ page, followBusy, onOpen, onToggleFollow, gridMode, stacked }) => {
    const gradient    = ENTITY_GRADIENT[page.entity_type] || ENTITY_GRADIENT.default;
    const accent      = ENTITY_ACCENT[page.entity_type] || ENTITY_ACCENT.default;
    const entityLabel = ENTITY_LABEL[page.entity_type] || page.entity_type || '';
    const isManaged   = ['super_admin', 'admin', 'editor'].includes(page.my_role);
    const isFollowing = !!page.is_following;

    const cardInner = (
        <div
            className="lcard"
            onClick={onOpen}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
            style={{ width: gridMode || stacked ? '100%' : '220px', flexShrink: 0, height: '100%', '--lcard-accent': accent }}
        >
            {/* Pale banner with two layered waves tinted by the entity colour */}
            <div className="lcard-banner">
                <svg className="lcard-wave" viewBox="0 0 400 80" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M0,44 C58,18 118,70 200,50 C282,30 342,62 400,40 L400,80 L0,80 Z" fill={accent} opacity="0.09" />
                    <path d="M0,60 C70,38 130,76 210,60 C290,44 350,72 400,56 L400,80 L0,80 Z" fill={accent} opacity="0.15" />
                </svg>

                {page.logo_url ? (
                    <img
                        src={page.logo_url}
                        alt={page.name}
                        className="lcard-logo"
                        style={{ background: '#fff' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                ) : (
                    <div className="lcard-logo" style={{ background: gradient }}>
                        {initials(page.name)}
                    </div>
                )}
                {entityLabel && (
                    <div className="lcard-chip" style={{ right: '14px' }}>
                        {entityLabel.toUpperCase()}
                    </div>
                )}
                {isFollowing && (
                    <div className="lcard-chip" style={{ left: '72px', letterSpacing: 0, fontWeight: 600 }}>
                        <i className="pi pi-check" style={{ fontSize: '0.5rem' }} />Following
                    </div>
                )}
            </div>

            {/* Body. flex:1 + margin-top:auto on the button pins the CTA to the
                bottom so cards in a row line up regardless of tagline length. */}
            <div className="lcard-body">
                <div className="lcard-name">{page.name}</div>

                {/* Star rating (shows only when backend returns average_rating) */}
                <StarDisplay rating={page.average_rating} count={page.rating_count || 0} />

                {page.hq_city && (
                    <div className="lcard-meta">
                        <i className="pi pi-map-marker" style={{ fontSize: '0.68rem' }} />
                        {page.hq_city}
                    </div>
                )}
                <div className="lcard-meta">
                    <i className="pi pi-users" style={{ fontSize: '0.68rem' }} />
                    {page.follower_count || 0} followers
                </div>

                {page.tagline && <div className="lcard-tagline">{page.tagline}</div>}

                {/* Follow / manage button */}
                <button
                    className={`lcard-btn ${isFollowing && !isManaged ? 'lcard-btn-ghost' : 'lcard-btn-primary'}`}
                    style={{ cursor: followBusy ? 'wait' : 'pointer', marginTop: 'auto' }}
                    onClick={(e) => { e.stopPropagation(); if (isManaged) { onOpen(); } else { onToggleFollow(e); } }}
                >
                    {isManaged ? (
                        <><i className="pi pi-cog" style={{ fontSize: '0.65rem' }} />Manage</>
                    ) : isFollowing ? (
                        <><i className="pi pi-check" style={{ fontSize: '0.65rem' }} />Following</>
                    ) : (
                        <><i className="pi pi-plus" style={{ fontSize: '0.65rem' }} />Follow</>
                    )}
                </button>
            </div>
        </div>
    );

    if (gridMode) {
        return <div className="col-12 sm:col-6 md:col-4 lg:col-3">{cardInner}</div>;
    }
    return cardInner;
};

/* ── See more card (end of horizontal scroll row) ────────────────────── */
const SeeMoreCard = ({ label, onClick }) => (
    <div
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        style={{
            width: '90px',
            flexShrink: 0,
            borderRadius: '12px',
            border: '1.5px dashed var(--surface-300)',
            background: 'var(--surface-50)',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '230px',
            color: 'var(--primary-color)',
            transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--primary-50)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface-50)'; }}
    >
        <i className="pi pi-arrow-right" style={{ fontSize: '1.25rem' }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 600, textAlign: 'center', lineHeight: 1.3, padding: '0 8px' }}>{label}</span>
    </div>
);

/* ── Discovery skeleton ──────────────────────────────────────────────── */
const DiscoverySkeleton = () => (
    <div>
        {/* Loan product rail skeleton — matches .hub-rail's card shell */}
        <div className="hub-rail">
            <div className="hub-rail-track">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                    <div key={i} className="flex flex-column align-items-center" style={{ gap: '8px', minWidth: '88px' }}>
                        <Skeleton shape="circle" size="66px" />
                        <Skeleton width="62px" height="0.78rem" />
                    </div>
                ))}
            </div>
        </div>
        {/* Feature cards skeleton */}
        <div className="grid mb-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="col-12 md:col-4">
                    <Skeleton height="270px" borderRadius="16px" />
                </div>
            ))}
        </div>
        <Skeleton height="86px" borderRadius="16px" className="mb-4" />
        {/* Horizontal rows skeleton */}
        {[1, 2, 3].map((row) => (
            <div key={row} className="mb-5">
                <Skeleton width="200px" height="1rem" className="mb-2" />
                <div style={{ display: 'flex', gap: '14px', overflow: 'hidden' }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} width="220px" height="230px" style={{ flexShrink: 0, borderRadius: '12px' }} />
                    ))}
                </div>
            </div>
        ))}
    </div>
);

/* ── Managed card (My Companies tab) ─────────────────────────────────── */
const ManagedCard = ({ page, onOpen, onDelete }) => {
    const isSuper      = page.my_role === 'super_admin';
    const role         = page.my_role ? ROLE_META[page.my_role] : null;
    const entityLabel  = ENTITY_LABEL[page.entity_type] || page.entity_type;
    const entitySev    = ENTITY_SEVERITY[page.entity_type] || 'secondary';
    const gradient     = ENTITY_GRADIENT[page.entity_type] || ENTITY_GRADIENT.default;
    const accent       = ENTITY_ACCENT[page.entity_type] || ENTITY_ACCENT.default;

    return (
        <div className="col-12 md:col-6 lg:col-4">
            {/* Same .lcard visual system as the Discover cards — pale wave banner,
                top-left logo tile, light pill badge — so the two tabs read as one
                product. Only the footer differs: Manage + delete instead of Follow. */}
            <div
                className="lcard"
                onClick={onOpen}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
                style={{ height: '100%', '--lcard-accent': accent }}
            >
                <div className="lcard-banner">
                    <svg className="lcard-wave" viewBox="0 0 400 80" preserveAspectRatio="none" aria-hidden="true">
                        <path d="M0,44 C58,18 118,70 200,50 C282,30 342,62 400,40 L400,80 L0,80 Z" fill={accent} opacity="0.09" />
                        <path d="M0,60 C70,38 130,76 210,60 C290,44 350,72 400,56 L400,80 L0,80 Z" fill={accent} opacity="0.15" />
                    </svg>

                    {page.logo_url ? (
                        <img src={page.logo_url} alt={page.name} className="lcard-logo" style={{ background: '#fff' }} onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                        <div className="lcard-logo" style={{ background: gradient }}>
                            {initials(page.name)}
                        </div>
                    )}

                    {role && (
                        <div className="lcard-chip" style={{ right: '14px', letterSpacing: 0 }}>
                            <i className={role.icon} style={{ fontSize: '0.55rem' }} />
                            {role.label}
                        </div>
                    )}
                </div>

                <div className="lcard-body">
                    <div className="lcard-name">{page.name}</div>

                    {page.average_rating > 0 && (
                        <StarDisplay rating={page.average_rating} count={page.rating_count || 0} />
                    )}

                    <div className="lcard-meta">
                        <i className="pi pi-users" style={{ fontSize: '0.68rem' }} />
                        {page.follower_count ?? 0} followers
                        {page.hq_city && (
                            <>
                                <span style={{ opacity: 0.45 }}>•</span>
                                <i className="pi pi-map-marker" style={{ fontSize: '0.68rem' }} />
                                {page.hq_city}
                            </>
                        )}
                    </div>

                    {page.tagline && <div className="lcard-tagline">{page.tagline}</div>}

                    {entityLabel && (
                        <div>
                            <Tag value={entityLabel} severity={entitySev} style={{ fontSize: '0.65rem', padding: '0.15rem 0.45rem' }} />
                        </div>
                    )}

                    <div className="flex gap-2 align-items-center" style={{ marginTop: 'auto' }}>
                        <button
                            className="lcard-btn lcard-btn-primary"
                            onClick={(e) => { e.stopPropagation(); onOpen(); }}
                        >
                            <i className="pi pi-cog" style={{ fontSize: '0.7rem' }} />Manage
                        </button>
                        {isSuper && (
                            <Button
                                icon="pi pi-trash"
                                size="small"
                                outlined
                                severity="danger"
                                tooltip="Delete page"
                                tooltipOptions={{ position: 'top' }}
                                onClick={onDelete}
                                aria-label="Delete page"
                                style={{ flexShrink: 0, width: '2.4rem', height: '2.15rem', borderRadius: '10px' }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ── Skeleton card ───────────────────────────────────────────────────── */
const SkeletonCard = () => (
    <div className="col-12 md:col-6 lg:col-4">
        {/* Mirrors the real card's geometry: 96px banner, 48px logo tile inset
            15px from the top-left, then the body stack. */}
        <div style={{ border: '1px solid var(--surface-border)', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{ position: 'relative' }}>
                <Skeleton width="100%" height="96px" borderRadius="0" />
                <div style={{ position: 'absolute', top: '15px', left: '15px' }}>
                    <Skeleton width="48px" height="48px" borderRadius="13px" />
                </div>
            </div>
            <div className="flex flex-column" style={{ padding: '14px', gap: '7px' }}>
                <Skeleton width="70%" height="1rem" />
                <Skeleton width="55%" height="0.75rem" />
                <Skeleton width="100%" height="1.9rem" borderRadius="8px" />
                <Skeleton width="35%" height="0.9rem" borderRadius="6px" />
                <div className="flex gap-2 mt-1">
                    <Skeleton width="100%" height="2.15rem" borderRadius="10px" />
                    <Skeleton width="2.4rem" height="2.15rem" borderRadius="10px" />
                </div>
            </div>
        </div>
    </div>
);

/* ── Empty state ─────────────────────────────────────────────────────── */
const ManagedEmptyState = ({ canCreate, onCreate }) => (
    <div className="flex flex-column align-items-center text-center py-6 gap-3">
        <div style={{ width: '4.5rem', height: '4.5rem', borderRadius: '50%', background: 'var(--primary-50)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <i className="pi pi-shield" style={{ fontSize: '1.75rem', color: 'var(--primary-color)' }} />
        </div>
        <div>
            <div className="text-900 font-semibold">No pages to manage yet</div>
            <p className="text-600 text-sm m-0 mt-1" style={{ maxWidth: '28rem' }}>
                {canCreate
                    ? 'Create a company page to start sharing updates, products and team members with the network.'
                    : "You don't manage any company pages. Ask a page admin to add you as an editor."}
            </p>
        </div>
        {canCreate && <Button label="Create Page" icon="pi pi-plus" size="small" onClick={onCreate} />}
    </div>
);

export default CompanyPagesHub;
